import React, { useMemo, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { TableBody, TableRow, TableHead, TableCell, Grid, Box, Icon } from '@mui/material';
import { StyledTable, StyledGrayIcon, StyledHeaderCell, StyledActionHeaderCell } from 'components/common/Styles';
import { Button, Typography } from '@totalsoft/rocket-ui';
import InsuranceConventionCoefficientContainer from './InsuranceConventionCoefficientContainer';
import InsuranceConventionCoefficientModalComponent from './InsuranceConventionCoefficientModalComponent';
import { Add } from '@mui/icons-material';
import { INSURANCE_CONVENTION_ADDITIONAL_DATA } from 'features/insuranceConventions/edit/queries';
import {
  INSURANCE_CONVENTION_ADDITIONAL_DATA_SAVED,
  INSURANCE_CONVENTION_ADDITIONAL_DATA_DELETED
} from 'features/insuranceConventions/edit/subscriptions';
import { ADMIN_INSURANCE_CONVENTIONS_WRITE } from 'constants/rightsCodes';
import { conventionCurrencyCodeVar, editCoefficientVar } from 'features/insuranceConventions/edit/variables';
import { addCachedDetail, removeCachedDetail } from 'utils/apollo/cacheExtensionFunctions';
import { useToast } from 'hooks/toasts/useToast';
import { useQuery, useSubscription } from 'hooks/apollo';
import { useTranslation } from 'react-i18next';
import { useReactiveVar } from '@apollo/client';
import { usePermission } from 'providers/RightsHooks';

const InsuranceConventionCoefficientsContainer = ({ productId }) => {
  const addToast = useToast();
  const { t } = useTranslation();
  const canWrite = usePermission(ADMIN_INSURANCE_CONVENTIONS_WRITE);
  const currencyCode = useReactiveVar(conventionCurrencyCodeVar);

  // #region state
  const isNewConvention = useMemo(() => !productId, [productId]);
  const [coefficients, setCoefficients] = useState([]);
  // #endregion

  // #region queries
  const { loading } = useQuery(INSURANCE_CONVENTION_ADDITIONAL_DATA, {
    skip: !productId,
    variables: { productId },
    onCompleted: data => {
      setCoefficients(data?.insuranceConventionAdditionalData || []);
    }
  });
  // #endregion

  // #region subscriptions
  useSubscription(INSURANCE_CONVENTION_ADDITIONAL_DATA_SAVED, {
    skip: !productId,
    variables: { productId },
    onData: ({ data: { data }, client }) => {
      client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'genericFilterTemplateDetails' });
      client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'genericFilterTemplateGeneralData' });
      const updatedCoefficient = data?.insuranceConventionAdditionalDataSaved?.insuranceConventionAdditionalData;
      const isNewCoefficient = !coefficients.some(
        coefficient => coefficient.insuranceConventionAdditionalDataId === updatedCoefficient.insuranceConventionAdditionalDataId
      );
      addCachedDetail(
        client,
        { name: 'insuranceConventionAdditionalData', value: INSURANCE_CONVENTION_ADDITIONAL_DATA },
        { productId },
        x => x.insuranceConventionAdditionalDataId === updatedCoefficient.insuranceConventionAdditionalDataId,
        updatedCoefficient,
        ['insuranceConventionAdditionalDataId']
      );
      if (isNewCoefficient) setCoefficients(prev => [...prev, { ...updatedCoefficient }]);
      else
        setCoefficients(prev =>
          prev.map(coefficient =>
            coefficient.insuranceConventionAdditionalDataId === updatedCoefficient.insuranceConventionAdditionalDataId
              ? updatedCoefficient
              : coefficient
          )
        );
      editCoefficientVar({ open: false });
      addToast(t('InsuranceConventions.Details.Coefficients.CoefficientSaved'), 'success');
    }
  });

  useSubscription(INSURANCE_CONVENTION_ADDITIONAL_DATA_DELETED, {
    onData: ({ data: { data }, client }) => {
      removeCachedDetail(
        client,
        { name: 'insuranceConventionAdditionalData', value: INSURANCE_CONVENTION_ADDITIONAL_DATA },
        { productId },
        x => x.insuranceConventionAdditionalDataId === data?.insuranceConventionAdditionalDataDeleted?.insuranceConventionAdditionalDataId,
        ['insuranceConventionAdditionalDataId']
      );
      addToast(t('InsuranceConventions.Details.Coefficients.CoefficientDeleted'), 'success');
    }
  });
  // #endregion

  // #region handlers
  const handleAddNewCoefficient = useCallback(() => {
    editCoefficientVar({ open: true, coefficient: { insuranceConventionId: productId } });
  }, [productId]);

  // #endregion

  return (
    <>
      {isNewConvention ? (
        <Grid container flexDirection='column' gap={1} p={2}>
          <Box display='flex' gap={1} alignItems='center' justifyContent='center'>
            <Icon fontSize='small' color='primary'>
              error
            </Icon>
            <Typography color='textPrimary'>{t('InsuranceConventions.Details.Warning')}</Typography>
          </Box>
        </Grid>
      ) : (
        <StyledTable>
          <TableHead>
            <TableRow>
              <StyledHeaderCell>{t('InsuranceConventions.Details.Coefficients.Coefficient')}</StyledHeaderCell>
              <StyledHeaderCell>{t('InsuranceConventions.Details.Coefficients.Detail')}</StyledHeaderCell>
              <StyledHeaderCell>{t('InsuranceConventions.Details.Coefficients.RelativePercent')}</StyledHeaderCell>
              <StyledHeaderCell>{t('InsuranceConventions.Details.Coefficients.PercentFormula')}</StyledHeaderCell>
              <StyledHeaderCell colSpan={2}>
                {t('InsuranceConventions.Details.Coefficients.Amount', { currencyCode: currencyCode })}
              </StyledHeaderCell>
              <StyledHeaderCell>{t('InsuranceConventions.Details.Coefficients.AmountFormula')}</StyledHeaderCell>
              <StyledHeaderCell>{t('InsuranceConventions.Details.Coefficients.Default')}</StyledHeaderCell>
              <StyledActionHeaderCell align='right'>
                <Button
                  startIcon={<Add />}
                  onClick={handleAddNewCoefficient}
                  disabled={isNewConvention || !canWrite}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  {t('Add')}
                </Button>
              </StyledActionHeaderCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              {!isNewConvention && !loading && coefficients.length === 0 && (
                <TableCell colSpan={8}>
                  <Box display='flex' gap={1} alignItems='center' justifyContent='center'>
                    <StyledGrayIcon fontSize='small'>error</StyledGrayIcon>
                    <Typography color='textSecondary'>{t('InsuranceConventions.Details.AddValues')}</Typography>
                  </Box>
                </TableCell>
              )}
            </TableRow>
            {!isNewConvention &&
              !loading &&
              coefficients.length > 0 &&
              coefficients.map(coefficient => (
                <InsuranceConventionCoefficientContainer key={coefficient.insuranceConventionAdditionalDataId} coefficient={coefficient} />
              ))}
          </TableBody>
        </StyledTable>
      )}
      <InsuranceConventionCoefficientModalComponent coefficients={coefficients} />
    </>
  );
};

InsuranceConventionCoefficientsContainer.propTypes = {
  productId: PropTypes.number
};

export default InsuranceConventionCoefficientsContainer;
